import { ReactNode, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { Skeleton } from '../components/ui';
import { WaitlistState } from './states/WaitlistState';
import { DeniedState } from './states/DeniedState';

type GateStatus = 'pending' | 'invalid' | 'waitingList' | 'denied' | 'granted';

const RE_VERIFY_INTERVAL_MS = 30_000;

interface VerifyResult {
  isValid: boolean;
  role?: string | null;
  user?: { userMetadata?: { creationState?: string | null } } | null;
}

function resolveStatus(result: VerifyResult): GateStatus {
  if (!result.isValid) return 'invalid';
  if (result.role === 'admin') return 'granted';

  const creationState = result.user?.userMetadata?.creationState;
  if (creationState === 'waitingList') return 'waitingList';
  if (creationState === 'denied') return 'denied';
  return 'granted';
}

function GateSkeleton() {
  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-surface">
      <div className="flex w-full max-w-sm flex-col items-center gap-4 px-6">
        <Skeleton className="h-12 w-12 rounded-full" />
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3 w-56" />
      </div>
    </div>
  );
}

export function AuthGate({ children }: { children: ReactNode }) {
  const verifyToken = useAuthStore((state) => state.verifyToken);
  const [status, setStatus] = useState<GateStatus>('pending');
  const hasVerifiedRef = useRef(false);

  // Verify exactly once on mount. Guarded with a ref (rather than an
  // effect-cleanup abort flag) so React 18 StrictMode's mount/cleanup/mount
  // dance still only issues a single verifyToken call and still applies its
  // result once it resolves.
  useEffect(() => {
    if (hasVerifiedRef.current) return;
    hasVerifiedRef.current = true;
    verifyToken()
      .then((result) => {
        setStatus(resolveStatus(result));
      })
      .catch(() => {
        // A rejected verify should never strand the gate on the loading
        // skeleton — treat it the same as an explicit invalid response.
        setStatus('invalid');
      });
  }, [verifyToken]);

  // Re-verify on window focus, but never more often than every 30s, and
  // never via setInterval/polling — only in response to a real focus event.
  useEffect(() => {
    const handleFocus = () => {
      const { lastVerifiedAt } = useAuthStore.getState();
      if (Date.now() - lastVerifiedAt >= RE_VERIFY_INTERVAL_MS) {
        verifyToken()
          .then((result) => {
            setStatus(resolveStatus(result));
          })
          .catch(() => {
            setStatus('invalid');
          });
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [verifyToken]);

  if (status === 'pending') {
    return <GateSkeleton />;
  }

  if (status === 'invalid') {
    return <Navigate to="/login" replace />;
  }

  if (status === 'waitingList') {
    return <WaitlistState />;
  }

  if (status === 'denied') {
    return <DeniedState />;
  }

  return <>{children}</>;
}
